import { Injectable } from '@angular/core';
import { EolctDataService } from 'src/app/shared/data-service/eolct-data.service';
import { StoreService } from 'projects/dop-core/src/lib/store/ngrx-store.service';
import { SettingsKey } from 'src/app/core/store/settings-config.keys';
import { FeatureFlagKey } from 'src/app/core/store/settings-feature-flag.keys';
import { SettingConfigurations, SettingFeatureFlags } from 'projects/dop-menu/src/public-api';

@Injectable({
    providedIn: 'root'
})
export class SettingsService {

    constructor(
        private eolCtDataService: EolctDataService,
        private storeService: StoreService) { }

    private _configurations: Config[] = [];
    private _featureFlags: FeatureFlag[] = [];
    private settingConfigurations: SettingConfigurations;
    private settingFeatureFlags: SettingFeatureFlags;

    async loadSettings(): Promise<any> {
        await this.loadConfigurations();
        await this.loadFeatureFlags();
        return new Promise((resolve) => {
            return resolve(true);
        });
    }

    get Configurations() {
        return this.settingConfigurations;
    }

    get FeatureFlags() {
        return this.settingFeatureFlags;
    }

    async loadConfigurations(): Promise<any> {
        let settingKeys = '';
        Object.keys(SettingsKey).forEach(e => { settingKeys = settingKeys.concat(',' + SettingsKey[e]); });

        return new Promise((resolve) => {
            this.eolCtDataService.getConfigurations(settingKeys.substr(1)).subscribe((data: Config[]) => {
                data.forEach(config => {
                    this.storeService.Save('db_' + SettingsKey[config.configurationKey], config.configurationValue, true);
                });
                this._configurations = data;
                this.initializeConfigs();
                resolve(data);
            });
        });
    }

    async loadFeatureFlags(): Promise<FeatureFlag[]> {
        let keys = '';
        Object.keys(FeatureFlagKey).forEach(e => { keys = keys.concat(',' + FeatureFlagKey[e]); });

        return new Promise((resolve) => {
            this.eolCtDataService.getFeatureFlags(keys.substr(1)).subscribe((data: FeatureFlag[]) => {
                data.forEach(config => {
                    this.storeService.Save('db_' + FeatureFlagKey[config.flagName], config.flagValue, true);
                });
                this._featureFlags = data;
                this.initializeFeatureFlags();
                resolve(data);
            });
        });
    }

    initializeFeatureFlags() {
        this.settingFeatureFlags = new SettingFeatureFlags();
        this.settingFeatureFlags.enableFavouriteSampleManagement = this.getFeatureFlagValue(FeatureFlagKey.enableFavouriteSampleManagement);
        this.settingFeatureFlags.enableTestPsorting = this.getFeatureFlagValue(FeatureFlagKey.enableTestPsorting);
        this.settingFeatureFlags.manageOrderingConfigurations = this.getFeatureFlagValue(FeatureFlagKey.manageOrderingConfigurations);
    }

    getFeatureFlagValue(featureFlagName: string) {
        return this._featureFlags.filter(t => t.flagName.toLowerCase() === featureFlagName.toLowerCase()).shift()?.flagValue;
    }

    getSettingValue(configKey: SettingsKey): Promise<any> {
        return new Promise((resolve) => {
            this.storeService.Get('db_' + configKey).subscribe(data => {
                if (configKey === SettingsKey.ELIMSTransitionedLabsiteAccounts) {
                    resolve(JSON.parse(data.toString()));
                }
                else {
                    resolve(data);
                }
            });
        });
    }

    async initializeConfigs() {
        this.settingConfigurations = new SettingConfigurations();
        this.settingConfigurations.useResultsV2 = this.getConfigValue(SettingsKey.useResultsV2);
        this.settingConfigurations.useResults = this.getConfigValue(SettingsKey.useResults);
        this.settingConfigurations.useResultsConfiguration = this.getConfigValue(SettingsKey.useResultsConfiguration);
        this.settingConfigurations.enableSelfReport = this.getConfigValue(SettingsKey.enableSelfreport);
        this.settingConfigurations.enableDashboard = this.getConfigValue(SettingsKey.enableDashboard);
        this.settingConfigurations.enableOrderResultsView = this.getConfigValue(SettingsKey.enableOrderResultsView);
        this.settingConfigurations.useDocumentSharing = this.getConfigValue(SettingsKey.useDocumentSharing);
        this.settingConfigurations.resultViewAccounts = this.getConfigValue(SettingsKey.resultViewAccounts);

        this.settingConfigurations.enableOSIS = this.getConfigValue(SettingsKey.EnableOSIS);
        this.settingConfigurations.newWaysOfCreatingOrdersEnabled = this.getConfigValue(SettingsKey.newWaysOfCreatingOrdersEnabled);
        this.settingConfigurations.disableViewQuotation = this.getConfigValue(SettingsKey.DisableViewQuotation);
        this.settingConfigurations.enableProject = this.getConfigValue(SettingsKey.EnableProject);
        this.settingConfigurations.enableSampleTaker = this.getConfigValue(SettingsKey.EnableSampleTaker);
        this.settingConfigurations.disableLimitSets = this.getConfigValue(SettingsKey.disableLimitSets);
        this.settingConfigurations.enableMyStatistics = this.getConfigValue(SettingsKey.EnableMyStatistics);
        this.settingConfigurations.enableFloorplans = this.getConfigValue(SettingsKey.enableFloorplans);
        this.settingConfigurations.enableSampleStatusTracking = this.getConfigValue(SettingsKey.enableSampleStatusTracking);
        this.settingConfigurations.disableInvoices = this.getConfigValue(SettingsKey.disableInvoices);
        this.settingConfigurations.enableDropOff = this.getConfigValue(SettingsKey.enableDropOff);
        this.settingConfigurations.enableAlerts = this.getConfigValue(SettingsKey.enableAlerts);
        this.settingConfigurations.enableContactPage = this.getConfigValue(SettingsKey.enableContactPage);
        this.settingConfigurations.enableProductManagement = this.getConfigValue(SettingsKey.enableProductManagement);
        this.settingConfigurations.enableLabContactPage = this.getConfigValue(SettingsKey.EnableLabContactPage);
        this.settingConfigurations.eIMASLogonURL = this.getConfigValue(SettingsKey.EIMASLogonURL);
        this.settingConfigurations.eIMASClientApplicationID = this.getConfigValue(SettingsKey.EIMASClientApplicationID);
        this.settingConfigurations.enableAdvancedSupportPage = this.getConfigValue(SettingsKey.EnableAdvancedSupportPage);
        this.settingConfigurations.expressOrderingUrl = this.getConfigValue(SettingsKey.ExpressOrderingUrl);
        this.settingConfigurations.enableExpressOrdering = this.getConfigValue(SettingsKey.EnableExpressOrdering);
        this.settingConfigurations.configureSortPatternUri = this.getConfigValue(SettingsKey.ConfigureSortPatternUri);
        this.settingConfigurations.productManagementEIMASLogonURL = this.getConfigValue(SettingsKey.ProductManagementEIMASLogonURL);
        this.settingConfigurations.manageFavouriteOrders = this.getConfigValue(SettingsKey.ManageFavouriteOrders);
        this.settingConfigurations.usePartialOrderEntry = this.getConfigValue(SettingsKey.usePartialOrderEntry);
        this.settingConfigurations.useOfferFileExport = this.getConfigValue(SettingsKey.useOfferFileExport);
        this.settingConfigurations.disableParameterSortings = this.getConfigValue(SettingsKey.DisableParameterSortings);
        this.settingConfigurations.enableLocalApplicationIntegration = this.getConfigValue(SettingsKey.EnableLocalApplicationIntegration);
        this.settingConfigurations.enableMultipleAssessments = this.getConfigValue(SettingsKey.enableMultipleAssessments);
        this.settingConfigurations.enableOSOIntegration = this.getConfigValue(SettingsKey.enableOSOIntegration);
        this.settingConfigurations.enableGuidedOrder = this.getConfigValue(SettingsKey.enableGuidedOrder);
        this.settingConfigurations.defaultOrderingService = this.getConfigValue(SettingsKey.DefaultOrderingService);
        this.settingConfigurations.enableSampleQuantityDisplay = this.getConfigValue(SettingsKey.EnableSampleQuantityDisplay);
        this.settingConfigurations.FrequentlyUsedDenUnits = this.getConfigCommaSeparateValue(SettingsKey.FrequentlyUsedDenUnits)
            .slice(0, 8);
        this.settingConfigurations.FrequentlyUsedNumUnits = this.getConfigCommaSeparateValue(SettingsKey.FrequentlyUsedNumUnits)
            .slice(0, 8);

        this.settingConfigurations.MaxSelectionOfAnalyses = this.getConfigValueAsNumber(SettingsKey.MaxSelectionOfAnalyses);
        this.settingConfigurations.EnableSchedulingRecurrentOrder = this.getConfigValue(SettingsKey.EnableSchedulingRecurrentOrder);
        this.settingConfigurations.elimsTransitionedLabsiteAccounts = JSON.parse(this.getConfigValue(SettingsKey.ELIMSTransitionedLabsiteAccounts));
        // this.settingConfigurations.elimsTransitionedLabsiteAccounts = [{LabsiteCode: 'EUUS38', AccountCodes:['A008546179PF']}];
    }

    getConfigValue(configKey: string): any {
        const data = this._configurations
            .filter(t => t.configurationKey.toLowerCase() === configKey.toLowerCase())
            .shift()?.configurationValue;
        if (data && data.toLowerCase() === 'true') {
            return true;
        } else if (data && data.toLowerCase() === 'false') {
            return false;
        } else {
            return data;
        }
    }

    getConfigCommaSeparateValue(configKey: string): string[] {
        const value = this.getConfigValue(configKey) as string;
        if (value && value.trim() !== '') {
            return value.split(',').filter(x => x && x.trim() !== '');
        }

        return [];
    }

    getConfigValueAsNumber(configKey: string): number {
        const value = this.getConfigValue(configKey) as string;

        if (value === null || value === undefined || value.trim() === '' || value === '0') {
            return 50; // Return default if any of these conditions are met
        }

        // Attempt to parse the string to a number
        const parsedValue = parseFloat(value);

        // Check if the parsing was successful
        if (!isNaN(parsedValue)) {
            return parsedValue; // Return the parsed number
        } else {
            return 50; // Return default value if parsing fails
        }
    }
}

export class Config {
    configurationKey: string;
    configurationValue: string;
}

export class FeatureFlag {
    flagName: string;
    flagValue: boolean;
}
